import React, { useState } from "react";
import { Copy, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const n = (v) => Number(v) || 0;
export const loadTotalMiles = (l) => n(l.total_miles) || n(l.miles) || (n(l.dead_head_miles ?? l.dead_head) + n(l.loaded_miles));
export const loadRpm = (l) => n(l.rpm) || (loadTotalMiles(l) ? +(n(l.rate) / loadTotalMiles(l)).toFixed(2) : 0);

export const BREAKDOWN_SECTIONS = [
  { title: "Load", fields: [["reference", "Load Number#"], ["driver", "Driver Name", true]] },
  { title: "Shipper", fields: [["shipper_name", "Shipper Name"], ["shipper_address", "Address"], ["pickup_date", "Date"], ["pickup_time", "Time"], ["pickup_number", "Pickup#"], ["appointment_number", "Appointment#"], ["reference_number", "Reference#"]] },
  { title: "Receiver", fields: [["receiver_name", "Receiver Name"], ["receiver_address", "Address"], ["delivery_date", "Date"], ["delivery_time", "Time"], ["delivery_number", "Delivery#"]] },
  { title: "Route & Pay", fields: [["map", "Map"], ["dead_head_miles", "Dead Head"], ["loaded_miles", "Loaded Miles"], ["total_miles", "Total Miles"], ["rate", "Rate"], ["rpm", "RPM"]] },
  { title: "Freight", fields: [["weight", "Weight"], ["temp", "Temp"], ["commodity", "Commodity"], ["equipment", "Equipment"], ["comment", "Comment"]] },
];

const show = (l, k) => {
  if (k === "total_miles") return loadTotalMiles(l) || "";
  if (k === "rpm") return loadRpm(l) ? `$${loadRpm(l)}` : "";
  if (k === "rate") return n(l.rate) ? `$${n(l.rate).toLocaleString()}` : "";
  if (k === "dead_head_miles") return l.dead_head_miles ?? l.dead_head ?? "";
  if (k === "shipper_address") return l.shipper_address || l.origin || "";
  if (k === "receiver_address") return l.receiver_address || l.destination || "";
  if (k === "map") {
    const a = show(l, "shipper_address"), b = show(l, "receiver_address");
    return l.map || (a && b ? `https://www.google.com/maps/dir/${encodeURIComponent(a)}/${encodeURIComponent(b)}` : "");
  }
  return l[k] ?? "";
};

export function breakdownText(l) {
  const plain = ["reference", "driver", "rate", "rpm"];
  return BREAKDOWN_SECTIONS.flatMap((sec) => sec.fields).map(([k, label]) => `${plain.includes(k) ? "" : "> "}${label}${k === "driver" ? " -" : ":"} ${show(l, k) || ""}`).join("\n");
}

export function LoadBreakdownView({ load, compact }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(breakdownText(load)); } catch (e) { /* clipboard blocked */ }
    setCopied(true); toast.success("Breakdown copied"); setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden" data-testid="load-breakdown">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-white">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Load Breakdown</p>
        <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700" data-testid="breakdown-copy">{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? "Copied" : "Copy"}</button>
      </div>
      <div className={`p-4 grid gap-x-8 gap-y-4 font-mono text-[13px] ${compact ? "grid-cols-1" : "sm:grid-cols-2"}`}>
        {BREAKDOWN_SECTIONS.map((sec) => (
          <div key={sec.title}>
            <p className="font-sans text-[10px] font-bold uppercase tracking-[0.16em] text-blue-600 mb-1.5">{sec.title}</p>
            {sec.fields.map(([k, label]) => {
              const v = show(load, k);
              return (
                <div key={k} className="grid grid-cols-[112px_minmax(0,1fr)] gap-2 leading-6">
                  <span className="text-slate-400">{label}:</span>
                  {k === "map" && v ? <a href={v} target="_blank" rel="noreferrer" className="text-blue-600 truncate hover:underline">Open Route</a> : <span className="text-slate-900 break-words min-w-0" data-testid={`bd-${k}`}>{v || "—"}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function LoadBreakdownForm({ value, onChange, hideDriver }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const inp = "w-full h-9 rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30";
  return (
    <div className="space-y-5" data-testid="load-breakdown-form">
      {BREAKDOWN_SECTIONS.map((sec) => (
        <div key={sec.title}>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-600 mb-2">{sec.title}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sec.fields.filter(([, , isDriver]) => !(isDriver && hideDriver)).map(([k, label, isDriver]) => (
              <label key={k} className={k === "comment" || k.endsWith("_address") ? "sm:col-span-2 lg:col-span-3" : ""}>
                <span className="block text-xs font-semibold text-slate-500 mb-1">{label}</span>
                {isDriver ? <input value={value.driver || ""} disabled className={`${inp} bg-slate-50 text-slate-500`} placeholder="Assigned in Load Management" /> : (
                  <input value={value[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={`${inp} ${["rate", "rpm", "total_miles", "loaded_miles", "dead_head_miles", "reference"].includes(k) ? "font-mono" : ""}`} data-testid={`bd-input-${k}`} />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
